import type { FC, KeyboardEvent } from 'react'
import type { ThesaurusEntry } from '../../types/wordTools'

interface SenseCardProps {
  entry: ThesaurusEntry
  onClick: (entry: ThesaurusEntry) => void
}

/**
 * Focusable card displaying a thesaurus sense entry.
 * Shows sense number, category, definition, and optional subcategory.
 * Activates on click or Enter/Space keypress.
 */
export const SenseCard: FC<SenseCardProps> = ({ entry, onClick }) => {
  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick(entry)
    }
  }

  return (
    <button
      data-testid={`sense-card-${entry.number}`}
      onClick={() => onClick(entry)}
      onKeyDown={handleKeyDown}
      className="w-full text-left p-3 bg-white border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      aria-label={`Sense ${entry.number}: ${entry.category} - ${entry.sense}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {/* Sense number and category */}
          <div className="text-sm font-medium text-gray-700 mb-1">
            {entry.number} • {entry.category}
          </div>

          {/* Sense definition (clamped to 3 lines) */}
          <div className="text-sm text-gray-600 line-clamp-3">
            {entry.sense}
          </div>

          {/* Optional subcategory */}
          {entry.subcategory && (
            <div className="text-xs text-gray-500 mt-1">
              {entry.subcategory}
            </div>
          )}
        </div>

        {/* Chevron arrow */}
        <div className="flex-shrink-0 text-gray-400 mt-1">
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      </div>
    </button>
  )
}
